import { useModalDismiss } from '../hooks/useModalDismiss';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faMinus, faInfoCircle, faPercent } from '@fortawesome/free-solid-svg-icons';

const AdjustmentDetailModal = ({ isOpen, onClose, adjustment, showAdminInfo = true }) => {
    // Escape only: this overlay has never closed on a backdrop click.
    useModalDismiss(onClose, { backdropClose: false, enabled: isOpen && !!adjustment });

    if (!isOpen || !adjustment) return null;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isAdvance = adjustment.type === 'advance_frais_deplacement';
    // Retenue de garantie (F2F): withheld share of a survey, carried to the next batch — not a penalty
    const isRetenue = adjustment.type === 'survey_exclusion' && adjustment.kind === 'retenue';
    const isBonus = adjustment.amount > 0 && adjustment.type !== 'carryover' && !isAdvance;
    const isPenalty = adjustment.amount < 0 && !isRetenue;
    const isCarryover = adjustment.type === 'carryover';
    const fmtPct = (p) => Number(p || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

    const formatReason = (reason) => {
        if (!reason) return 'Non spécifié';
        const reasonLabels = {
            'unpaid_confirmation': 'Non payé confirmé',
            'insufficient_amount': 'Montant insuffisant',
            'deleted_survey_deduction': 'Déduction pour enquêtes supprimées',
            'rejected': 'Rejeté',
            'survey_closed': 'Enquête fermée',
            'carryover': 'Montant reporté',
            'bonus': 'Bonus',
            'penalty': 'Pénalité',
            'advance_frais_deplacement': 'Frais de déplacement',
            'survey_exclusion': 'Exclusion enquête',
            'deleted_paid_submission_clawback': 'Récupération de soumissions payées supprimées',
            'pending_deduction_recovery': 'Récupération de déduction non recouvrée',
        };
        return reasonLabels[reason] || formatRoleNames(reason);
    };

    const roleLabels = {
        'chef_terrain_superieur': 'Chef Terrain Supérieur',
        'administrateur_general_superieur': 'Business Officer',
        'administrateur_general': 'Administrateur Général',
        'resp_admin': 'Responsable Admin',
        'resp_cati': 'Responsable CATI',
        'resp_controle': 'Responsable Contrôle',
        'chef_terrain': 'Chef Terrain',
        'admin_it': 'Admin IT',
        'charge_etude_superieur': 'Chargé d\'Étude Supérieur',
        'charge_etude': 'Chargé d\'Étude',
        'utilisateur': 'Utilisateur',
    };

    const formatRoleNames = (text) => {
        if (!text || typeof text !== 'string') return text;
        let formatted = text;
        Object.entries(roleLabels).forEach(([role, label]) => {
            formatted = formatted.replaceAll(role, label);
        });
        return formatted;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isRetenue ? 'bg-yellow-100' : isBonus ? 'bg-green-100' : isPenalty ? 'bg-red-100' : 'bg-gray-100'
                        }`}>
                            <FontAwesomeIcon
                                icon={isRetenue ? faPercent : isBonus ? faPlus : isPenalty ? faMinus : faInfoCircle}
                                className={`text-lg ${
                                    isRetenue ? 'text-yellow-700' : isBonus ? 'text-green-600' : isPenalty ? 'text-red-600' : 'text-gray-600'
                                }`}
                            />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                {isRetenue ? 'Retenue de garantie' : isCarryover ? 'Montant reporté' : isAdvance ? 'Frais de déplacement' : isBonus ? 'Bonus' : isPenalty ? 'Pénalité' : 'Ajustement'}
                            </h3>
                            <p className="text-sm text-gray-500">
                                Détails de l'ajustement
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Amount */}
                    <div className="text-center">
                        <div className={`text-4xl font-bold ${
                            isRetenue ? 'text-yellow-700' : isBonus ? 'text-green-600' : isPenalty ? 'text-red-600' : 'text-gray-600'
                        }`}>
                            {isBonus ? '+' : ''}{formatCurrency(adjustment.amount)}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            {isRetenue ? 'Montant retenu — gardé en garantie' : isCarryover ? 'Montant reporté' : isBonus ? 'Montant du bonus' : isPenalty ? 'Montant de la pénalité' : 'Montant de l\'ajustement'}
                        </p>
                    </div>

                    {isRetenue && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 space-y-1">
                            <div>
                                Retenue de <strong>{fmtPct(adjustment.percentage)} %</strong>
                                {adjustment.survey_title ? <> sur l'enquête <strong>{adjustment.survey_title}</strong></> : null}
                                {adjustment.base_amount ? <> (montant de l'enquête : {formatCurrency(adjustment.base_amount)})</> : null}.
                            </div>
                            {Math.abs(parseFloat(adjustment.qty || 0)) > 0 && (
                                <div>Équivaut à {Math.abs(parseFloat(adjustment.qty)).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} complète(s), comptée(s) comme payée(s) quand la garantie sera versée.</div>
                            )}
                            <div>Ce montant n'est pas perdu : il est gardé en garantie et sera payé quand le chef terrain marquera le travail comme terminé.</div>
                        </div>
                    )}

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Type */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-1">Type</div>
                            <div className="font-medium text-gray-900">
                                {adjustment.kind === 'retenue'
                                    ? `Retenue ${Number(adjustment.percentage || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %${adjustment.survey_title ? ` (${adjustment.survey_title})` : ''}`
                                    : (adjustment.type_label || formatReason(adjustment.type))}
                            </div>
                        </div>

                        {/* Date */}
                        {adjustment.created_at && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600 mb-1">Date</div>
                                <div className="font-medium text-gray-900">
                                    {formatDate(adjustment.created_at)}
                                </div>
                            </div>
                        )}

                        {/* Applied By - Admin only, but not for carryover */}
                        {showAdminInfo && !isCarryover && (adjustment.applied_by || adjustment.by || adjustment.type === 'deleted_survey_deduction') && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600 mb-1">Appliqué par</div>
                                <div className="font-medium text-gray-900">
                                    {adjustment.type === 'deleted_survey_deduction' ? 'Système' : (adjustment.applied_by || `Utilisateur #${adjustment.by}`)}
                                </div>
                                {adjustment.applied_by_email && adjustment.type !== 'deleted_survey_deduction' && (
                                    <div className="text-sm text-gray-500 mt-1">
                                        {adjustment.applied_by_email}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Batch Reference - Admin only */}
                        {showAdminInfo && adjustment.batch_ref && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600 mb-1">Lot</div>
                                <div className="font-medium text-gray-900">
                                    {adjustment.batch_ref}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Reason */}
                    <div>
                        <div className="text-sm text-gray-600 mb-2">Motif</div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-blue-900 leading-relaxed whitespace-pre-wrap">
                                {formatReason(adjustment.reason)}
                            </p>
                        </div>
                    </div>

                    {/* Carryover Quantity - Only for carryover adjustments */}
                    {isCarryover && adjustment.carryover_qty !== null && adjustment.carryover_qty > 0 && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <div className="text-sm text-purple-600 mb-2 font-medium">Quantité reportée</div>
                            {(() => {
                                const fmtN = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
                                const fmtQty = (qty, paymentType) => paymentType === 'per_day'
                                    ? `${fmtN(qty)} jour${qty > 1 ? 's' : ''}`
                                    : `${fmtN(qty)} enquête${qty > 1 ? 's' : ''}`;
                                const hasPerDay = adjustment.carryover_surveys?.some(s => s.payment_type === 'per_day');
                                const hasPerComplete = adjustment.carryover_surveys?.some(s => s.payment_type !== 'per_day');
                                const mixedTypes = hasPerDay && hasPerComplete;
                                // For totals with mixed types, show split
                                const fmtTotal = () => {
                                    if (!mixedTypes) return fmtQty(adjustment.carryover_qty, hasPerDay ? 'per_day' : 'per_complete');
                                    const days = adjustment.carryover_surveys.filter(s => s.payment_type === 'per_day').reduce((s, sv) => s + sv.qty, 0);
                                    const completes = adjustment.carryover_surveys.filter(s => s.payment_type !== 'per_day').reduce((s, sv) => s + sv.qty, 0);
                                    return `${completes} enquête${completes > 1 ? 's' : ''} + ${days} jour${days > 1 ? 's' : ''}`;
                                };

                                return adjustment.carryover_surveys && adjustment.carryover_surveys.length > 0 ? (
                                    <div className="space-y-2">
                                        {adjustment.carryover_surveys.length === 1 ? (
                                            <div>
                                                <div className="text-lg font-bold text-purple-900">
                                                    {fmtQty(adjustment.carryover_qty, adjustment.carryover_surveys[0].payment_type)}
                                                </div>
                                                <div className="text-sm text-purple-700 mt-1">
                                                    {adjustment.carryover_surveys[0].survey_title}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="text-lg font-bold text-purple-900 mb-2">
                                                    {fmtTotal()} au total
                                                </div>
                                                <div className="space-y-1.5">
                                                    {adjustment.carryover_surveys.map((survey, idx) => (
                                                        <div key={idx} className="flex justify-between items-center text-sm bg-white bg-opacity-50 rounded px-2 py-1">
                                                            <span className="text-purple-800 font-medium">{survey.survey_title}</span>
                                                            <span className="text-purple-900 font-semibold">{fmtQty(survey.qty, survey.payment_type)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-lg font-bold text-purple-900">
                                        {Number(adjustment.carryover_qty || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} enquête{adjustment.carryover_qty > 1 ? 's' : ''}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Survey Info - Only for deletion deductions */}
                    {adjustment.type === 'deleted_survey_deduction' && adjustment.survey_title && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="text-sm text-red-600 mb-1 font-medium">Enquête concernée</div>
                            <div className="font-medium text-red-900">
                                {adjustment.survey_title}
                            </div>
                            {adjustment.deleted_qty && (
                                <div className="text-sm text-red-700 mt-1">
                                    Quantité supprimée: {Math.abs(adjustment.deleted_qty)} enquête{Math.abs(adjustment.deleted_qty) > 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Clawed-back submissions - who they belong to (supervisor or interviewer) */}
                    {adjustment.type === 'deleted_paid_submission_clawback' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            {adjustment.survey_title && (
                                <>
                                    <div className="text-sm text-red-600 mb-1 font-medium">Enquête concernée</div>
                                    <div className="font-medium text-red-900 mb-3">{adjustment.survey_title}</div>
                                </>
                            )}
                            <div className="text-sm text-red-600 mb-1 font-medium">Personnes concernées</div>
                            {(() => {
                                const persons = (adjustment.persons || []).filter(p => p.type !== 'unknown');
                                return persons.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {persons.map((p, i) => (
                                            <span key={i} className="inline-flex items-center px-2 py-1 rounded-md bg-red-100 text-red-800 text-xs font-medium">
                                                {p.type === 'supervisor'
                                                    ? 'Prestataire'
                                                    : `${p.name}${p.ref ? ` (${p.ref})` : ''}`} : {p.count} soumission{p.count > 1 ? 's' : ''}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-red-700">Information indisponible</div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Additional Notes */}
                    {adjustment.notes && (
                        <div>
                            <div className="text-sm text-gray-600 mb-2">Notes additionnelles</div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {formatRoleNames(adjustment.notes)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors duration-200"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdjustmentDetailModal;
